import {last} from 'lodash';

import {PromiseCompletionSource} from '../../helpers/asyncHelpers';

/*
 * Interface.
 */

export interface Lock {
  readonly isLocked: boolean;
  lockAsync(): Promise<void>;
  lockAsync<R>(callback: () => R | Promise<R>): Promise<R>;
  unlock(): void;
}

/*
 * API.
 */

/** Creates a mutex. */
export function createLock(): Lock {
  return new PromiseLock();
}

/*
 * Implementation.
 */

class PromiseLock implements Lock {
  private queue: PromiseCompletionSource<void>[] = [];

  get isLocked() {
    return this.queue.length > 0;
  }

  /* eslint-disable no-dupe-class-members */
  /** @deprecated Use the callback-style lockAsync() instead. */
  lockAsync(): Promise<void>;
  lockAsync<R>(callback: () => R | Promise<R>): Promise<R>;
  async lockAsync(callback?: () => unknown): Promise<unknown> {
    // Get the turn ahead of us.
    const prevTurn = last(this.queue);

    // Add ourselves to the queue.
    const turn = new PromiseCompletionSource<void>();
    this.queue.push(turn);

    // Wait for the turn ahead of us.
    if (prevTurn) {
      await prevTurn.promise;
    }

    // Handle non-callback-style invocation.
    if (!callback) {
      return undefined;
    }

    try {
      return await callback();
    } finally {
      this.unlock();
    }
  }
  /* eslint-enable no-dupe-class-members */

  /** @deprecated Use the callback-style lockAsync() instead. */
  unlock() {
    const firstTurn = this.queue.shift();
    if (!firstTurn) {
      throw new Error('Unbalanced unlock(), strongly recommend using the callback-style lockAsync()');
    }

    // Complete the turn at the front of the queue.
    firstTurn.resolve();
  }
}

/**
 * Acquires and releases multiple mutexes at once.
 *
 * Warning: for deadlock avoidance, the locks should be sorted to ensure a consistent acquisition order.
 */
export async function lockAllAsync<R>(
  locks: ReadonlyArray<Lock>,
  callback: () => R | Promise<R>,
): Promise<R> {
  await Promise.all(locks.map((lock) => lock.lockAsync()));
  try {
    return await callback();
  } finally {
    locks.forEach((lock) => lock.unlock());
  }
}
